import crossIcon from "src/assets/cross.svg";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/context/store";
import { ordActions } from "src/context/order-slice";
import { useToast } from "src/hooks/useToast";
import { useEventStatus } from "src/hooks/useEventStatus";
import { Autocomplete, Button } from "src/components";
import TextArea from "src/components/ui/text-area.component";
import { getOrderDetailsByOrderCode2, stockistSaoAccept, stockistSaoReject } from "src/services/order-api";

export const STOCKIST_SAO_REJECT_REASONS = [
  { key: "PAYMENT_ISSUE", name: "Counter Outstanding payment/Payment issue" },
  { key: "NO_DEALING_WITH_COUNTER", name: "Do not have dealing with counter" },
  { key: "COUNTER_NOT_WILLING", name: "Counter not willing to purchase" },
  { key: "INCORRECT_ORDER_DETAILS", name: "Incorrect order details" },
  { key: "PRODUCTS_NOT_DEALT", name: "Do not deal with these products" },
  { key: "NO_DELIVERY_TO_LOCATION", name: "Do not delivery to this location" },
  { key: "OTHERS", name: "Others" },
];

const MAX_REMARKS = 500;

const StockistSaoAckDialog = ({ mode, onCloseModal }: { mode: "accept" | "reject"; onCloseModal: () => void }) => {
  const isReject = mode === "reject";
  const { toast } = useToast();
  const dispatch = useDispatch();
  const { withEventCheck } = useEventStatus();
  const { orderDetailsFiltered } = useSelector((state: RootState) => state.ord);
  const orderDetails = orderDetailsFiltered?.[0];
  const [remarks, setRemarks] = useState<string>("");
  const [selectedReason, setSelectedReason] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleSubmit = async () => {
    const trimmedRemarks = remarks.trim();
    if (isReject && !selectedReason) {
      toast({ description: "Please select a reject reason", variant: "destructive" });
      return;
    }
    if ((!isReject || selectedReason?.key === "OTHERS") && !trimmedRemarks) {
      toast({ description: "Please enter remarks", variant: "destructive" });
      return;
    }
    if (trimmedRemarks.length > MAX_REMARKS) {
      toast({ description: `Remarks cannot exceed ${MAX_REMARKS} characters`, variant: "destructive" });
      return;
    }
    setIsLoading(true);
    try {
      const response = isReject
        ? await stockistSaoReject(
            {
              data: {
                order_id: orderDetails?.order_id_int,
                reject_reason: selectedReason?.key,
                remarks: trimmedRemarks,
              },
              event_type: "ORDER_STOCKIST_SAO_REJECT",
              primary_key: orderDetails?.order_id_int,
            },
            orderDetails?.company_code
          )
        : await stockistSaoAccept(
            {
              data: { order_id: orderDetails?.order_id_int, remarks: trimmedRemarks },
              event_type: "ORDER_STOCKIST_SAO_ACCEPT",
              primary_key: orderDetails?.order_id_int,
            },
            orderDetails?.company_code
          );
      const response2 = await withEventCheck(
        () => {
          return getOrderDetailsByOrderCode2(orderDetails?.order_code, orderDetails?.company_code);
        },
        response?.data?.data?.eventStreamCreated?.streamId,
        orderDetails?.company_code
      );
      dispatch(ordActions.setOrderDetailsFiltered([response2?.data?.data]));
      toast({
        description: isReject ? "Stockist SAO rejected successfully" : "Stockist SAO accepted successfully",
        variant: "default",
      });
      onCloseModal();
    } catch (error: any) {
      console.log(error);
      toast({
        description: error?.response?.data?.message || `Failed to ${isReject ? "reject" : "accept"} Stockist SAO`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-y-2 mb-4 w-[340px] mx-auto px-6 bg-white p-4 rounded-xl">
      <div className="flex justify-between items-center">
        <p className="text-normal py-1 font-semibold text-[#49484C]">{isReject ? "Reject Stockist SAO" : "Accept Stockist SAO"}</p>
        <Button className="bg-transparent -mr-3" onClick={onCloseModal}>
          <img src={crossIcon} alt="cross" className="w-5 h-5 font-bold invert cursor-pointer" />
        </Button>
      </div>
      {isReject && (
        <div>
          <p className="text-sm mb-2 mt-2 font-normal text-[#666666]">Reject Reason</p>
          <Autocomplete
            placeholder="Select reason"
            suggestionList={STOCKIST_SAO_REJECT_REASONS}
            displayKey="name"
            uniqueKey="key"
            onSelectionChange={(e: any) => setSelectedReason(e)}
          />
        </div>
      )}
      <div>
        <p className="text-sm mb-2 mt-2 font-normal text-[#666666]">
          Remarks{!isReject || selectedReason?.key === "OTHERS" ? " *" : " (optional)"}
        </p>
        <TextArea
          placeholder="Enter remarks"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value.slice(0, MAX_REMARKS))}
          className="h-24"
        />
      </div>
      <Button loaderSize="small" isLoading={isLoading} className="text-white bg-[#8051FF] mt-2" onClick={handleSubmit}>
        {isReject ? "Reject" : "Accept"}
      </Button>
    </div>
  );
};

export default StockistSaoAckDialog;
